import type { OverlayEvent } from '@shared/types'

interface DeferredEntry {
  event: OverlayEvent
  enqueuedAt: number
}

/**
 * Буфер отложенных событий: хранит события, для которых ещё нет плагина.
 *
 * Жизненный цикл события:
 *  1. defer(event)         — плагин не зарегистрирован, сохраняем
 *  2. flush(pluginType)    — плагин готов, возвращаем все события типа
 *  3. drop(pluginType)     — плагин недоступен, удаляем без replay
 *
 * TTL-очистка: flush автоматически отбрасывает устаревшие записи.
 * types(): используется при реконнекте для повторного plugin_request.
 *
 * Узкое место:
 *   TTL проверяется лениво — только при flush/drop.
 *   Для очень редких событий добавь фоновый purge-таймер если нужно.
 */
export class DeferredEventBuffer {
  private buffer = new Map<string, DeferredEntry[]>()

  /** Сохраняет событие в буфере */
  defer(event: OverlayEvent): void {
    const list = this.buffer.get(event.type) ?? []
    list.push({ event, enqueuedAt: Date.now() })
    this.buffer.set(event.type, list)
  }

  /**
   * Извлекает и возвращает все живые события типа.
   * Устаревшие (> ttlMs) молча отбрасываются.
   */
  flush(pluginType: string, ttlMs: number): OverlayEvent[] {
    const entries = this.buffer.get(pluginType) ?? []
    this.buffer.delete(pluginType)

    if (ttlMs <= 0) return entries.map((e) => e.event)

    const now = Date.now()
    return entries
      .filter((e) => now - e.enqueuedAt < ttlMs)
      .map((e) => e.event)
  }

  /**
   * Удаляет все события типа без replay.
   * Вызывается при получении plugin_not_available.
   */
  drop(pluginType: string): number {
    const count = this.buffer.get(pluginType)?.length ?? 0
    this.buffer.delete(pluginType)
    return count
  }

  /** Все типы у которых есть отложенные события — для replay при реконнекте */
  types(): string[] {
    return Array.from(this.buffer.keys())
  }

  has(pluginType: string): boolean {
    return this.buffer.has(pluginType)
  }

  size(pluginType: string): number {
    return this.buffer.get(pluginType)?.length ?? 0
  }
}

export const deferredEventBuffer = new DeferredEventBuffer()
